import {selectedLocationFields,municipalityFields} from '../../../geo/locationFields';
import { validarRecorrido } from '../CenterControl/validarRecorrido';
import { resolverUbicacion } from '../CenterControl/useCiudadesTraslado';
import useGeografiaTraslado from '../CenterControl/useGeografiaTraslado';
import { Alert as GeografiaAlert } from 'antd';
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  DatePicker,
  TimePicker,
  message,
  Card,
  Typography,
  Spin,
} from "antd";
import {
  EnvironmentOutlined,
  SwapOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  AimOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import { coordinateValidationRule } from "../../../utils/coordinates";
import api from "../../../axiosConfig";
import { useUser } from "../../../UserContext";
import { useServerTime } from "../../../ServerTimeContext";

import MapDialog from "../../../Resources/GoogleApi/MapDialog";
import MapRoute from "../CenterControl/MapDialog";

// Importar estilos del TrasladoDialog
import "../CenterControl/TrasladoDialog.css";

const { Title } = Typography;

const { Option } = Select;
const { TextArea } = Input;
const normalizarLugar = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLocaleLowerCase('es');

/**
 * isAtLeast15MinutesAfter:
 * Retorna true si 'later' es al menos 15 minutos después de 'earlier'.
 */
function isAtLeast15MinutesAfter(earlier, later) {
  if (!earlier || !later) return true;
  return later.diff(earlier, "minute") >= 15;
}

function getDepartamentoName(deptos, id) {
  const dep = deptos.find((d) => d.id === id);
  return dep ? dep.nombre : id || "";
}

function getCiudadName(ciudades, id) {
  const city = ciudades.find((c) => c.id === id);
  return city ? city.nombre : id || "";
}

function getInstitucionName(instituciones, val, isTemporal) {
  // Si es temporal, val es directamente el texto que el usuario escribió
  if (isTemporal) return val || "";
  // Si no es temporal, val debería ser un ID => buscamos en la lista
  const inst = instituciones.find((i) => i.id === val);
  return inst ? inst.nombre : val || "";
}

function EditarTrasladoDialog({ open, onClose, traslado, onUpdated }) {
  const [form] = Form.useForm();
  const solicitudesLugar = useRef({ origen: 0, destino: 0 });
  useEffect(() => {
    return () => { solicitudesLugar.current.origen++; solicitudesLugar.current.destino++; };
  }, [open, traslado?.id]);
  const geografiaTraslado = useGeografiaTraslado(form, traslado?.autorizacion_id, traslado?.trayecto);
  const { user } = useUser();
  const { getServerTime } = useServerTime();

  // =========================================
  // Estado para el traslado completo cargado desde API
  // =========================================
  const [trasladoCompleto, setTrasladoCompleto] = useState(null);
  const [loadingTraslado, setLoadingTraslado] = useState(false);

  // =========================================
  // 1) minFecha / maxFecha (rango Autorización)
  // =========================================
  const [minFecha, setMinFecha] = useState(null);
  const [maxFecha, setMaxFecha] = useState(null);

  // =========================================
  // Estados para la UI
  // =========================================
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudadesOrigen, setCiudadesOrigen] = useState([]);
  const [ciudadesDestino, setCiudadesDestino] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [catalogoCiudades, setCatalogoCiudades] = useState([]);
  const valoresRecorrido = Form.useWatch(values => values, { form, preserve: true }) || {};
  const trayectoActual = trasladoCompleto?.trayecto || traslado?.trayecto || 'Ida';
  const erroresRecorrido = validarRecorrido(geografiaTraslado.contexto, trayectoActual, valoresRecorrido, departamentos, catalogoCiudades);
  const erroresIntercambio = validarRecorrido(geografiaTraslado.contexto, trayectoActual, valoresRecorrido, departamentos, catalogoCiudades, true);
  const ciudadesHistoricasCargadas = ['origen','destino'].every(lado => {
    const dep = resolverUbicacion(departamentos, trasladoCompleto?.[lado + '_departamento']);
    return !dep || catalogoCiudades.some(c => Number(c.departamento_id) === Number(dep.id));
  });
  const erroresHistoricos = geografiaTraslado.contexto?.configurada && trasladoCompleto && departamentos.length && ciudadesHistoricasCargadas
    ? validarRecorrido(geografiaTraslado.contexto, trayectoActual, trasladoCompleto, departamentos, catalogoCiudades) : [];
  const opcionesUbicacion = (lista, tipo, lado) => {
    const permitidas = geografiaTraslado.filtrar(lista, tipo, lado);
    const campo = lado + '_' + tipo;
    const valor = valoresRecorrido[campo];
    const opciones = permitidas.map(item => ({ value: item.id, label: item.nombre }));
    if (valor != null && valor !== '' && !opciones.some(o => o.value === valor)) {
      const item = resolverUbicacion(lista, valor);
      const permitido = item && permitidas.some(p => Number(p.id) === Number(item.id));
      const nombre = item?.nombre || (/^\d+$/.test(String(valor)) ? 'Ubicación pendiente de seleccionar' : String(valor));
      opciones.push({ value: valor, label: nombre + (permitido ? '' : ' (fuera de lo permitido)'), disabled: true });
    }
    return opciones;
  };


  // Para búsqueda de lugares temporales
  const [searchTextOrigen, setSearchTextOrigen] = useState("");
  const [searchTextDestino, setSearchTextDestino] = useState("");
  const [origenEsTemporal, setOrigenEsTemporal] = useState(false);
  const [destinoEsTemporal, setDestinoEsTemporal] = useState(false);

  // Para mostrar la ruta en mapa
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [distance, setDistance] = useState(null); // ← km (string)
  const [duration, setDuration] = useState(null); // ← min (string)

  const [mapVisible, setMapVisible] = useState(false);
  const [mapMode, setMapMode] = useState(null); // "origen" / "destino" / null
  const [isSubmitting, setIsSubmitting] = useState(false);

  // =========================================
  // 2) Función para calcular distancia/duración
  // =========================================
  const updateDistanceAndDuration = useCallback(async () => {
    const oriStr = form.getFieldValue("origen_coordenadas");
    const desStr = form.getFieldValue("destino_coordenadas");
    if (!oriStr || !desStr) return;

    const oriParts = oriStr.split(",").map((x) => x.trim());
    const desParts = desStr.split(",").map((x) => x.trim());
    if (oriParts.length < 2 || desParts.length < 2) return;

    const latOri = parseFloat(oriParts[0]);
    const lngOri = parseFloat(oriParts[1]);
    const latDes = parseFloat(desParts[0]);
    const lngDes = parseFloat(desParts[1]);

    if (isNaN(latOri) || isNaN(lngOri) || isNaN(latDes) || isNaN(lngDes)) {
      return;
    }

    const newOrigin = { lat: latOri, lng: lngOri };
    const newDestination = { lat: latDes, lng: lngDes };

    // Aproximación en línea recta (igual que al crear el traslado). No hay servicio de rutas: la antigua
    // petición a /route daba siempre 404 y mostraba un aviso en cada edición.
    const R = 6371; // Radio de la Tierra en km
    const dLat = ((newDestination.lat - newOrigin.lat) * Math.PI) / 180;
    const dLon = ((newDestination.lng - newOrigin.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((newOrigin.lat * Math.PI) / 180) *
        Math.cos((newDestination.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const approxDist = (R * c).toFixed(2);
    const approxDur = (approxDist * 2).toFixed(2); // Estimación básica: 30 km/h promedio

    setDistance(approxDist);
    setDuration(approxDur);
    setOrigin(newOrigin);
    setDestination(newDestination);
  }, [form]);

  // =========================================
  // 3) useEffect para cargar departamentos e instituciones
  // =========================================
  useEffect(() => {
    if (!open) return;

    // Cargar departamentos e instituciones
    const fetchData = async () => {
      try {
        const respDeptos = await api.get("/departamentos");
        setDepartamentos(respDeptos.data);

        const respInst = await api.get("/instituciones");
        setInstituciones(respInst.data || []);
      } catch (error) {
        console.error("Error al cargar datos para edición:", error);
        message.error("Error al cargar datos para edición.");
      }
    };
    fetchData();
  }, [open]);

  // =========================================
  // 3.1) useEffect para cargar traslado completo desde API
  // =========================================
  useEffect(() => {
    if (!open || !traslado?.id) {
      setTrasladoCompleto(null);
      return;
    }

    // Cargar traslado completo desde la API para tener todos los campos
    const fetchTrasladoCompleto = async () => {
      setLoadingTraslado(true);
      try {
        const resp = await api.get(`/traslados/${traslado.id}`);
        setTrasladoCompleto(resp.data);
      } catch (error) {
        console.error("Error al cargar traslado completo:", error);
        // Si falla, usar los datos que ya tenemos
        setTrasladoCompleto(traslado);
      } finally {
        setLoadingTraslado(false);
      }
    };
    fetchTrasladoCompleto();
  }, [open, traslado?.id]);

  // Función helper para cargar ciudades de un departamento
  const fetchCiudades = useCallback(async (departamentoId) => {
    if (!departamentoId) return [];
    try {
      const response = await api.get(
        `/ciudades?departamento_id=${departamentoId}`
      );
      const datos = Array.isArray(response.data) ? response.data : [];
      setCatalogoCiudades(prev => [...prev.filter(c => Number(c.departamento_id) !== Number(departamentoId)), ...datos]);
      return datos;
    } catch (error) {
      console.error("Error al obtener ciudades:", error);
      return [];
    }
  }, []);

  // =========================================
  useEffect(() => {
    if (!open) return;
    let vigente = true;
    for (const lado of ['origen', 'destino']) {
      const dep = resolverUbicacion(departamentos, valoresRecorrido[lado + '_departamento']);
      if (!dep) continue;
      fetchCiudades(dep.id).then(datos => {
        if (vigente) (lado === 'origen' ? setCiudadesOrigen : setCiudadesDestino)(datos);
      });
    }
    return () => { vigente = false; };
  }, [open, departamentos, valoresRecorrido.origen_departamento, valoresRecorrido.destino_departamento, fetchCiudades]);

  // 4) Efecto para traer la Autorización y setear minFecha / maxFecha
  // =========================================
  useEffect(() => {
    if (!open || !trasladoCompleto) return;

    if (trasladoCompleto.autorizacion_id) {
      const fetchAut = async () => {
        try {
          const resp = await api.get(
            `/autorizaciones/${trasladoCompleto.autorizacion_id}`
          );
          const aut = resp.data;

          const startDate = aut.inicio_fecha_autorizacion
            ? dayjs(aut.inicio_fecha_autorizacion, "YYYY-MM-DD")
            : null;
          const endDate = aut.final_fecha_autorizacion
            ? dayjs(aut.final_fecha_autorizacion, "YYYY-MM-DD")
            : null;

          setMinFecha(startDate);
          setMaxFecha(endDate);

          // Fechas de autorización obtenidas
        } catch (err) {
          console.error("Error al obtener la autorización:", err);
        }
      };
      fetchAut();
    }
  }, [open, trasladoCompleto]);

  // =========================================
  // 5) Efecto para precargar datos del traslado
  // =========================================
  useEffect(() => {
    // Usar trasladoCompleto que viene de la API con todos los campos
    if (open && trasladoCompleto && !loadingTraslado) {
      form.setFieldsValue({
        fecha: trasladoCompleto.fecha ? dayjs(trasladoCompleto.fecha, "YYYY-MM-DD") : null,
        hora_recogida: trasladoCompleto.hora_recogida
          ? dayjs(trasladoCompleto.hora_recogida, "HH:mm")
          : null,
        hora_llegada: trasladoCompleto.hora_llegada
          ? dayjs(trasladoCompleto.hora_llegada, "HH:mm")
          : null,

        origen_lugar: trasladoCompleto.origen_lugar,
        origen_tipo_lugar: trasladoCompleto.origen_tipo_lugar ?? null,
        origen_institucion_id: trasladoCompleto.origen_institucion_id ?? null,
        origen_direccion: trasladoCompleto.origen_direccion,
        origen_departamento: trasladoCompleto.origen_departamento,
        origen_ciudad: trasladoCompleto.origen_ciudad,
        origen_localidad: trasladoCompleto.origen_localidad,
        origen_barrio: trasladoCompleto.origen_barrio,
        origen_vereda_corregimiento: trasladoCompleto.origen_vereda_corregimiento || "",
        origen_indicaciones: trasladoCompleto.origen_indicaciones,
        origen_coordenadas: coordsToString(trasladoCompleto.origen_coordenadas),

        destino_lugar: trasladoCompleto.destino_lugar,
        destino_tipo_lugar: trasladoCompleto.destino_tipo_lugar ?? null,
        destino_institucion_id: trasladoCompleto.destino_institucion_id ?? null,
        destino_direccion: trasladoCompleto.destino_direccion,
        destino_departamento: trasladoCompleto.destino_departamento,
        destino_ciudad: trasladoCompleto.destino_ciudad,
        destino_localidad: trasladoCompleto.destino_localidad,
        destino_barrio: trasladoCompleto.destino_barrio,
        destino_vereda_corregimiento: trasladoCompleto.destino_vereda_corregimiento || "",
        destino_indicaciones: trasladoCompleto.destino_indicaciones,
        destino_coordenadas: coordsToString(trasladoCompleto.destino_coordenadas),

        // Nuevo campo agregado
        observaciones_traslado: trasladoCompleto.observaciones_traslado || "",
        indicaciones_conductor: trasladoCompleto.indicaciones_conductor || "",
      });

      setOrigenEsTemporal(!isInstitutionId(trasladoCompleto.origen_lugar));
      setDestinoEsTemporal(!isInstitutionId(trasladoCompleto.destino_lugar));

      // Conservar la ubicación guardada hasta que el operador elija otro lugar.

      // Recalcular la distancia/duración
      updateDistanceAndDuration();
    } else if (!open) {
      form.resetFields();
      setDistance(null);
      setDuration(null);
      setTrasladoCompleto(null);
    }
  }, [open, trasladoCompleto, loadingTraslado, form, updateDistanceAndDuration]);

  // =========================================
  // 6) Restricción de fecha en DatePicker
  // =========================================
  const disabledDate = (current) => {
    if (!current) return false;
    // Usar hora del servidor para validaciones
    const today = getServerTime().startOf("day");
    const isDisabled = (
      current < today ||
      (minFecha && current < minFecha.startOf("day")) ||
      (maxFecha && current > maxFecha.endOf("day"))
    );

    // Validación de fecha completada

    return isDisabled;
  };

  // =========================================
  // 7) Guardar (PUT) la edición del traslado
  // =========================================
  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      if (erroresRecorrido.length) {
        message.warning(erroresRecorrido.join(' '));
        return;
      }

    const realOrigenLugar = getInstitucionName(
      instituciones,
      values.origen_lugar,
      origenEsTemporal
    );
    const realDestinoLugar = getInstitucionName(
      instituciones,
      values.destino_lugar,
      destinoEsTemporal
    );

    const origenNombre = (realOrigenLugar || "").toString().trim().toLowerCase();
    const destinoNombre = (realDestinoLugar || "").toString().trim().toLowerCase();

    if (origenNombre === "sin destino") {
      message.error(
        "No se puede editar el traslado cuando la institución origen es 'Sin Destino'."
      );
      setIsSubmitting(false);
      return;
    }
    if (destinoNombre === "sin destino") {
      message.error(
        "No se puede editar el traslado cuando la institución destino es 'Sin Destino'."
      );
      setIsSubmitting(false);
      return;
    }

      // 1) Calcula la distancia y duración como ya lo haces
      const distanceNumber = distance ? parseFloat(distance) : 0;
      const durationNumber = duration ? Math.round(parseFloat(duration)) : 0;

      // 2) Obtén los nombres reales (si procede)
      const origenDepartamentoName = getDepartamentoName(
        departamentos,
        values.origen_departamento
      );
      const destinoDepartamentoName = getDepartamentoName(
        departamentos,
        values.destino_departamento
      );

      const origenCiudadName = getCiudadName(
        ciudadesOrigen,
        values.origen_ciudad
      );
      const destinoCiudadName = getCiudadName(
        ciudadesDestino,
        values.destino_ciudad
      );

      // 3) Construye el objeto final con los nombres en vez de IDs
      const updatedData = {
        fecha: values.fecha ? values.fecha.format("YYYY-MM-DD") : null,
        hora_recogida: values.hora_recogida
          ? values.hora_recogida.format("HH:mm")
          : null,
        hora_llegada: values.hora_llegada
          ? values.hora_llegada.format("HH:mm")
          : null,

        // Si en tu tabla guardas 'origen_departamento' como texto y NO como ID
        // entonces en lugar de values.origen_departamento:
        origen_departamento: origenDepartamentoName,
        origen_ciudad: origenCiudadName,
        geografia_version_esperada: form.getFieldValue('geografia_version_esperada'),
        origen_lugar: realOrigenLugar,
        origen_tipo_lugar: form.getFieldValue('origen_tipo_lugar'),
        origen_institucion_id: form.getFieldValue('origen_institucion_id'),
        origen_direccion: values.origen_direccion,
        origen_localidad: values.origen_localidad,
        origen_barrio: values.origen_barrio,
        origen_vereda_corregimiento: values.origen_vereda_corregimiento,
        origen_indicaciones: values.origen_indicaciones,
        origen_coordenadas: values.origen_coordenadas,

        destino_departamento: destinoDepartamentoName,
        destino_ciudad: destinoCiudadName,
        destino_lugar: realDestinoLugar,
        destino_tipo_lugar: form.getFieldValue('destino_tipo_lugar'),
        destino_institucion_id: form.getFieldValue('destino_institucion_id'),
        destino_direccion: values.destino_direccion,
        destino_localidad: values.destino_localidad,
        destino_barrio: values.destino_barrio,
        destino_vereda_corregimiento: values.destino_vereda_corregimiento,
        destino_indicaciones: values.destino_indicaciones,
        destino_coordenadas: values.destino_coordenadas,

        distancia_estimada: distanceNumber,
        duracion_estimada: durationNumber,

        observaciones_traslado: values.observaciones_traslado,
        indicaciones_conductor: values.indicaciones_conductor,
        usuario_id: user.id,
      };

      // 4) Llamada al endpoint PUT (usar trasladoCompleto o traslado.id como fallback)
      const trasladoId = trasladoCompleto?.id || traslado?.id;
      await api.put(`/traslados/${trasladoId}`, updatedData);

      message.success("Traslado editado correctamente.");
      if (onUpdated) {
        onUpdated({ ...(trasladoCompleto || traslado), ...updatedData });
      }
      onClose();
    } catch (error) {
      console.error("Error al editar el traslado:", error);
      message.error("Error al editar el traslado.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // =========================================
  // 8) Validaciones personalizadas de horas
  // =========================================
  const validateHoraRecogida = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value) return Promise.resolve();

      // Usar hora del servidor para validaciones
      const serverNow = getServerTime();
      // Validar que no sea hora pasada si la fecha es hoy
      const fecha = getFieldValue("fecha");
      if (fecha && fecha.isSame(serverNow, "day")) {
        const horaSeleccionada = serverNow
          .hour(value.hour())
          .minute(value.minute())
          .second(0);
        if (horaSeleccionada.isBefore(serverNow)) {
          return Promise.reject(
            new Error(
              "No se puede programar una hora de recogida en el pasado"
            )
          );
        }
      }

      const horaLlegada = getFieldValue("hora_llegada");
      // "La hora de recogida no puede ser después de la hora de llegada"
      if (horaLlegada && horaLlegada.isBefore(value)) {
        return Promise.reject(
          new Error(
            "La hora de recogida no puede ser después de la hora de llegada"
          )
        );
      }
      // Ejemplo: "La hora de retorno de recogida debe ser al menos 15 minutos después de la hora de recogida"
      const horaRetornoRecogida = getFieldValue("hora_retorno_recogida");
      if (
        horaRetornoRecogida &&
        !isAtLeast15MinutesAfter(value, horaRetornoRecogida)
      ) {
        return Promise.reject(
          new Error(
            "La hora de retorno de recogida debe ser al menos 15 minutos después de la hora de recogida"
          )
        );
      }
      return Promise.resolve();
    },
  });

  const validateHoraLlegada = ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value) return Promise.resolve();

      // Usar hora del servidor para validaciones
      const serverNow = getServerTime();
      // Validar que no sea hora pasada si la fecha es hoy
      const fecha = getFieldValue("fecha");
      if (fecha && fecha.isSame(serverNow, "day")) {
        const horaSeleccionada = serverNow
          .hour(value.hour())
          .minute(value.minute())
          .second(0);
        if (horaSeleccionada.isBefore(serverNow)) {
          return Promise.reject(
            new Error(
              "No se puede programar una hora de llegada en el pasado"
            )
          );
        }
      }

      const horaRecogida = getFieldValue("hora_recogida");
      if (
        horaRecogida &&
        !isAtLeast15MinutesAfter(horaRecogida, value)
      ) {
        return Promise.reject(
          new Error(
            "La hora de llegada debe ser al menos 15 minutos después de la hora de recogida"
          )
        );
      }
      const horaRetornoRecogida = getFieldValue("hora_retorno_recogida");
      if (
        horaRetornoRecogida &&
        !isAtLeast15MinutesAfter(value, horaRetornoRecogida)
      ) {
        return Promise.reject(
          new Error(
            "La hora de retorno de recogida debe ser al menos 15 minutos después de la hora de llegada"
          )
        );
      }
      return Promise.resolve();
    },
  });

  // Revalidar horas cuando cambia la fecha
  const handleFechaChange = () => {
    form.validateFields(["hora_recogida", "hora_llegada"]).catch(() => {
      // Los errores se mostrarán en los campos
    });
  };

  // =========================================
  // 9) Manejo de institución (origen/destino)
  // =========================================
  const handleOrigenChange = async (value) => {
    if (value === 'Domicilio') {
      const domicilio = geografiaTraslado.domicilio?.('origen');
      if (!domicilio) return;
      const solicitud = ++solicitudesLugar.current.origen;
      setOrigenEsTemporal(false);
      form.setFieldsValue({ origen_lugar: 'Domicilio', origen_tipo_lugar: 'domicilio', origen_institucion_id: null,
        origen_vereda_corregimiento: domicilio.vereda_corregimiento || '' });
      await fillInstitucionData('origen', domicilio, solicitud);
      return;
    }
    const solicitud = ++solicitudesLugar.current.origen;
    form.setFieldsValue({ origen_tipo_lugar: typeof value === 'number' ? 'institucion' : value === 'Domicilio' ? 'domicilio' : 'temporal', origen_institucion_id: typeof value === 'number' ? value : null });
    if (!value) {
      setOrigenEsTemporal(false);
      clearOrigenFields("");
      return;
    }
    if (isInstitutionId(value)) {
      setOrigenEsTemporal(false);
      form.setFieldsValue({ origen_lugar: value });
      try {
        const inst = await api.get(`/instituciones/${value}`);
        await fillInstitucionData("origen", inst.data, solicitud);
      } catch (error) {
        console.error("Error al cargar institución:", error);
      }
    } else {
      setOrigenEsTemporal(true);
      clearOrigenFields(value);
    }
  };

  const handleDestinoChange = async (value) => {
    if (value === 'Domicilio') {
      const domicilio = geografiaTraslado.domicilio?.('destino');
      if (!domicilio) return;
      const solicitud = ++solicitudesLugar.current.destino;
      setDestinoEsTemporal(false);
      form.setFieldsValue({ destino_lugar: 'Domicilio', destino_tipo_lugar: 'domicilio', destino_institucion_id: null,
        destino_vereda_corregimiento: domicilio.vereda_corregimiento || '' });
      await fillInstitucionData('destino', domicilio, solicitud);
      return;
    }
    const solicitud = ++solicitudesLugar.current.destino;
    form.setFieldsValue({ destino_tipo_lugar: typeof value === 'number' ? 'institucion' : value === 'Domicilio' ? 'domicilio' : 'temporal', destino_institucion_id: typeof value === 'number' ? value : null });
    if (!value) {
      setDestinoEsTemporal(false);
      clearDestinoFields("");
      return;
    }
    if (isInstitutionId(value)) {
      setDestinoEsTemporal(false);
      form.setFieldsValue({ destino_lugar: value });
      try {
        const inst = await api.get(`/instituciones/${value}`);
        await fillInstitucionData("destino", inst.data, solicitud);
      } catch (error) {
        console.error("Error al cargar institución:", error);
      }
    } else {
      setDestinoEsTemporal(true);
      clearDestinoFields(value);
    }
  };

  const fillInstitucionData = async (prefix, data, solicitud) => {
    if (!data || solicitudesLugar.current[prefix] !== solicitud) return;
    const {
      departamento_id,
      ciudad_id,
      direccion,
      coordenadas,
      indicaciones,
      barrio,
      localidad,
      vereda_corregimiento,
    } = data;

    form.setFieldsValue({
      [`${prefix}_direccion`]: direccion || "",
      [`${prefix}_barrio`]: barrio || "",
      [`${prefix}_vereda_corregimiento`]: vereda_corregimiento || "",
      [`${prefix}_localidad`]: localidad || "",
      [`${prefix}_indicaciones`]: indicaciones || "",
      [`${prefix}_coordenadas`]: coordsToString(coordenadas),
    });

    if (departamento_id) {
      const newCities = await fetchCiudades(departamento_id);
      if (solicitudesLugar.current[prefix] !== solicitud) return;
      if (prefix === "origen") {
        setCiudadesOrigen(newCities);
      } else {
        setCiudadesDestino(newCities);
      }
      form.setFieldsValue({ [`${prefix}_departamento`]: departamento_id });
    }
    if (ciudad_id) {
      form.setFieldsValue({ [`${prefix}_ciudad`]: ciudad_id });
    }
  };

  // =========================================
  // 10) Limpiar campos (origen/destino)
  // =========================================
  const clearOrigenFields = (tempValue = "") => {
    form.setFieldsValue({
      origen_lugar: tempValue,
      origen_departamento: null,
      origen_ciudad: null,
      origen_localidad: "",
      origen_barrio: "",
      origen_vereda_corregimiento: "",
      origen_direccion: "",
      origen_indicaciones: "",
      origen_coordenadas: "",
    });
  };

  const clearDestinoFields = (tempValue = "") => {
    form.setFieldsValue({
      destino_lugar: tempValue,
      destino_departamento: null,
      destino_ciudad: null,
      destino_localidad: "",
      destino_barrio: "",
      destino_vereda_corregimiento: "",
      destino_direccion: "",
      destino_indicaciones: "",
      destino_coordenadas: "",
    });
  };

  const handleAgregarLugarTemporalOrigen = (texto) => {
    solicitudesLugar.current.origen++;
    form.setFieldsValue({ origen_tipo_lugar: 'temporal', origen_institucion_id: null });
    setSearchTextOrigen('');
    setOrigenEsTemporal(true);
    clearOrigenFields(texto.trim());
  };

  const handleAgregarLugarTemporalDestino = (texto) => {
    solicitudesLugar.current.destino++;
    form.setFieldsValue({ destino_tipo_lugar: 'temporal', destino_institucion_id: null });
    setSearchTextDestino('');
    setDestinoEsTemporal(true);
    clearDestinoFields(texto.trim());
  };

  // =========================================
  // 11) renderDropdownMenu
  // =========================================
  const renderDropdownMenu = (menu, searchText, lista, onAddLugarTemporal) => {
    const noExiste = !lista.some(
      (inst) => normalizarLugar(inst.nombre) === normalizarLugar(searchText)
    );
    return (
      <>
        {menu}
        {searchText.trim() !== "" && noExiste && (
          <Button
            type="text"
            icon={<PlusOutlined style={{ marginRight: 8 }} />}
            onClick={() => onAddLugarTemporal(searchText)}
            style={{
              padding: "8px 12px",
              borderTop: "1px solid #eee",
              width: "100%",
              textAlign: "left",
            }}
          >
            Agregar lugar temporal: <strong>{searchText}</strong>
          </Button>
        )}
      </>
    );
  };

  // =========================================
  // 12) Mapa (MapDialog) y selección de coords
  // =========================================
  const handleCloseMap = () => {
    setMapVisible(false);
  };

  const handleMapSelect = async (direccionMap, localidadMap, coordsMap, details) => {
    if (!['origen', 'destino'].includes(mapMode)) return false;
    const match = details?.catalog_match;
    if (match && !geografiaTraslado.filtrar([match.city], 'ciudad', mapMode).length) {
      message.warning('El municipio de la ubicación no está permitido para este recorrido.');
      return false;
    }
    if (match) {
      const cities = await fetchCiudades(match.department.id);
      if (mapMode === 'origen') setCiudadesOrigen(cities); else setCiudadesDestino(cities);
    }
    form.setFieldsValue({
      ...selectedLocationFields(direccionMap, localidadMap, coordsMap, details, `${mapMode}_`),
      ...municipalityFields(details, `${mapMode}_`, false),
    });
    updateDistanceAndDuration();
  };

  // =========================================
  // 13) Botón SWAP Origen-Destino
  // =========================================
  const swapOrigenDestino = () => {
    if (erroresIntercambio.length) {
      message.warning('No se puede intercambiar: ' + erroresIntercambio.join(' '));
      return;
    }
    solicitudesLugar.current.origen++;
    solicitudesLugar.current.destino++;
    const oFields = form.getFieldsValue([
      "origen_lugar",
      "origen_tipo_lugar",
      "origen_institucion_id",
      "origen_departamento",
      "origen_ciudad",
      "origen_localidad",
      "origen_barrio",
      "origen_vereda_corregimiento",
      "origen_direccion",
      "origen_indicaciones",
      "origen_coordenadas",
    ]);
    const dFields = form.getFieldsValue([
      "destino_lugar",
      "destino_tipo_lugar",
      "destino_institucion_id",
      "destino_departamento",
      "destino_ciudad",
      "destino_localidad",
      "destino_barrio",
      "destino_vereda_corregimiento",
      "destino_direccion",
      "destino_indicaciones",
      "destino_coordenadas",
    ]);

    form.setFieldsValue({
      // Origen => Destino
      origen_lugar: dFields.destino_lugar,
      origen_tipo_lugar: dFields.destino_tipo_lugar,
      origen_institucion_id: dFields.destino_institucion_id,
      origen_departamento: dFields.destino_departamento,
      origen_ciudad: dFields.destino_ciudad,
      origen_localidad: dFields.destino_localidad,
      origen_barrio: dFields.destino_barrio,
      origen_vereda_corregimiento: dFields.destino_vereda_corregimiento,
      origen_direccion: dFields.destino_direccion,
      origen_indicaciones: dFields.destino_indicaciones,
      origen_coordenadas: dFields.destino_coordenadas,

      // Destino => Origen
      destino_lugar: oFields.origen_lugar,
      destino_tipo_lugar: oFields.origen_tipo_lugar,
      destino_institucion_id: oFields.origen_institucion_id,
      destino_departamento: oFields.origen_departamento,
      destino_ciudad: oFields.origen_ciudad,
      destino_localidad: oFields.origen_localidad,
      destino_barrio: oFields.origen_barrio,
      destino_vereda_corregimiento: oFields.origen_vereda_corregimiento,
      destino_direccion: oFields.origen_direccion,
      destino_indicaciones: oFields.origen_indicaciones,
      destino_coordenadas: oFields.origen_coordenadas,
    });

    // Intercambiar también los arrays de ciudades para que el Select muestre el nombre correcto
    const tempCiudadesOrigen = ciudadesOrigen;
    setCiudadesOrigen(ciudadesDestino);
    setCiudadesDestino(tempCiudadesOrigen);

    // Intercambiar estados de lugar temporal
    const tempOrigenEsTemporal = origenEsTemporal;
    setOrigenEsTemporal(destinoEsTemporal);
    setDestinoEsTemporal(tempOrigenEsTemporal);

    updateDistanceAndDuration();
  };

  // =========================================
  // 14) Helpers para coords
  // =========================================
  function isInstitutionId(val) {
    return typeof val === 'number' && Number.isInteger(val) && val > 0;
  }

  function coordsToString(geo) {
    if (!geo) return "";
    
    // Si ya es string, devolverlo
    if (typeof geo === 'string') {
      return geo;
    }
    
    // Si es objeto GeoJSON con coordinates
    if (geo.coordinates && Array.isArray(geo.coordinates) && geo.coordinates.length === 2) {
      const [lng, lat] = geo.coordinates;
      return `${lat}, ${lng}`;
    }
    
    return "";
  }

  // =========================================
  // RENDER
  // =========================================
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="traslado-modal-header">
          Editar Traslado
        </div>
      }
      footer={[
        <Button
          key="cancelar"
          onClick={onClose}
          disabled={isSubmitting}
          className="cancelar-button"
        >
          Cancelar
        </Button>,
        <Button
          key="guardar"
          type="primary"
          onClick={handleSave}
          loading={isSubmitting}
          disabled={isSubmitting}
          className="programar-button"
        >
          Guardar
        </Button>,
      ]}
      width="90%"
      centered
      destroyOnHidden
      className="traslado-modal"
    >
      <Spin spinning={loadingTraslado} tip="Cargando datos del traslado...">
      <div className="traslado-modal-content">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{}}
      >
        {/* ============ Fecha y Horas ============ */}
        <Title level={4} className="traslado-section-title">
          <CalendarOutlined className="traslado-section-icon" />
          Fecha y Horarios
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Fecha"
              name="fecha"
              rules={[{ required: true, message: "Fecha obligatoria" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={disabledDate}
                onChange={handleFechaChange}
              />
            </Form.Item>
          </Col>

          {/* Hora Recogida */}
          <Col xs={24} sm={8}>
            <Form.Item
              label="Hora Recogida"
              name="hora_recogida"
              rules={[
                { required: true, message: "Hora de recogida obligatoria" },
                validateHoraRecogida,
              ]}
            >
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                inputReadOnly={false}
                onKeyDown={(e) => {
                  const value = e.target.value;
                  if (e.key === "Enter" && value) {
                    // Si son solo números
                    if (/^\d+$/.test(value)) {
                      let hours = Math.floor(value / 100);
                      let minutes = value % 100;

                      // Validar horas y minutos
                      if (hours >= 24) hours = 23;
                      if (minutes >= 60) minutes = 59;

                      // Crear el objeto dayjs con la hora
                      const time = dayjs().hour(hours).minute(minutes);
                      form.setFieldsValue({ hora_recogida: time });
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>

          {/* Hora Llegada */}
          <Col xs={24} sm={8}>
            <Form.Item
              label="Hora Llegada"
              name="hora_llegada"
              rules={[
                { required: true, message: "Hora de llegada obligatoria" },
                validateHoraLlegada,
              ]}
            >
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                inputReadOnly={false}
                onKeyDown={(e) => {
                  const value = e.target.value;
                  if (e.key === "Enter" && value) {
                    // Si son solo números
                    if (/^\d+$/.test(value)) {
                      let hours = Math.floor(value / 100);
                      let minutes = value % 100;

                      // Validar horas y minutos
                      if (hours >= 24) hours = 23;
                      if (minutes >= 60) minutes = 59;

                      // Crear el objeto dayjs con la hora
                      const time = dayjs().hour(hours).minute(minutes);
                      form.setFieldsValue({ hora_llegada: time });
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ============ ORIGEN/DESTINO ============ */}
              {erroresHistoricos.length > 0 && <GeografiaAlert type="warning" showIcon
                message="El recorrido guardado no coincide con las ubicaciones permitidas por la autorización."
                description={erroresHistoricos.join(' ') + ' Revise origen, destino y trayecto antes de guardar. No se corrige automáticamente.'} />}
              {geografiaTraslado.error && <GeografiaAlert type="warning" showIcon message={geografiaTraslado.error} />}
        <Row gutter={[16, 16]}>
          {/* ORIGEN */}
          <Col xs={24} sm={11}>
            <Title level={4} className="traslado-section-title">
              <AimOutlined className="traslado-section-icon" />
              Origen
            </Title>
            <Form.Item label="Lugar (Institución)" name="origen_lugar">
              <Select
                showSearch
                allowClear
                onSearch={(val) => setSearchTextOrigen(val)}
                filterOption={(input, option) => normalizarLugar(option.children).includes(normalizarLugar(input))}
                onChange={handleOrigenChange}
                dropdownRender={(menu) =>
                  renderDropdownMenu(
                    menu,
                    searchTextOrigen,
                    instituciones,
                    handleAgregarLugarTemporalOrigen
                  )
                }
              >
                {geografiaTraslado.domicilio?.('origen') && <Option key="domicilio" value="Domicilio" label="Domicilio">Domicilio</Option>}
                {geografiaTraslado.filtrar(instituciones, 'institucion', 'origen').map((inst) => (
                  <Option key={inst.id} value={inst.id}>
                    {inst.nombre}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {origenEsTemporal && (
              <Button
                icon={<EnvironmentOutlined />}
                style={{ marginBottom: 8 }}
                onClick={() => {
                  setMapMode("origen");
                  setMapVisible(true);
                }}
              >
                Buscar en el mapa (Origen)
              </Button>
            )}

            <Form.Item
              label="Departamento"
              name="origen_departamento"
              rules={[
                { required: true, message: "El departamento es obligatorio" },
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Seleccione un departamento"
                onChange={async (val) => {
                  form.setFieldsValue({ origen_ciudad: null });
                  const c = await fetchCiudades(val);
                  setCiudadesOrigen(c);
                }}
                filterOption={(input, option) => {
                  const label = option.label ? option.label.toLowerCase() : "";
                  const searchInput = input.toLowerCase();
                  return label.includes(searchInput);
                }}
                style={{ width: "100%" }}
              >
                {opcionesUbicacion(departamentos, 'departamento', 'origen').map(opcion => (
                  <Option key={opcion.value} value={opcion.value} label={opcion.label} disabled={opcion.disabled}>
                    {opcion.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Ciudad"
              name="origen_ciudad"
              rules={[{ required: true, message: "La ciudad es obligatoria" }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Seleccione una ciudad"
                filterOption={(input, option) => {
                  const label = option.label ? option.label.toLowerCase() : "";
                  const searchInput = input.toLowerCase();
                  return label.includes(searchInput);
                }}
                style={{ width: "100%" }}
              >
                {opcionesUbicacion(ciudadesOrigen, 'ciudad', 'origen').map(opcion => (
                  <Option key={opcion.value} value={opcion.value} label={opcion.label} disabled={opcion.disabled}>
                    {opcion.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Dirección"
              name="origen_direccion"
              rules={[
                { required: true, message: "La dirección es obligatoria" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Localidad" name="origen_localidad">
              <Input />
            </Form.Item>
            <Form.Item
              label="Barrio"
              name="origen_barrio"
              rules={[{ required: true, message: "El barrio es obligatorio" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Vereda/Corregimiento" name="origen_vereda_corregimiento">
              <Input placeholder="Opcional" />
            </Form.Item>
            <Form.Item label="Indicaciones" name="origen_indicaciones">
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item
              label="Coordenadas (lat, lng)"
              name="origen_coordenadas"
              rules={[
                { required: true, message: "Las coordenadas son obligatorias" },
                coordinateValidationRule(true),
              ]}
            >
              <Input placeholder="Ej: 4.6097, -74.0817" />
            </Form.Item>
          </Col>

          {/* Botón SWAP */}
          <Col xs={24} sm={2} className="traslado-swap-col">
            <Button
              icon={<SwapOutlined />}
              shape="circle"
              size="large"
              onClick={swapOrigenDestino}
              aria-label="Intercambiar origen y destino"
              disabled={erroresIntercambio.length > 0}
              title={erroresIntercambio.length ? 'No se puede intercambiar: ' + erroresIntercambio.join(' ') : 'Intercambiar origen y destino'}
            />
          </Col>

          {/* DESTINO */}
          <Col xs={24} sm={11}>
            <Title level={4} className="traslado-section-title">
              <AimOutlined className="traslado-section-icon" />
              Destino
            </Title>
            <Form.Item label="Lugar (Institución)" name="destino_lugar">
              <Select
                showSearch
                allowClear
                onSearch={(val) => setSearchTextDestino(val)}
                filterOption={(input, option) => normalizarLugar(option.children).includes(normalizarLugar(input))}
                onChange={handleDestinoChange}
                dropdownRender={(menu) =>
                  renderDropdownMenu(
                    menu,
                    searchTextDestino,
                    instituciones,
                    handleAgregarLugarTemporalDestino
                  )
                }
              >
                {geografiaTraslado.domicilio?.('destino') && <Option key="domicilio" value="Domicilio" label="Domicilio">Domicilio</Option>}
                {geografiaTraslado.filtrar(instituciones, 'institucion', 'destino').map((inst) => (
                  <Option key={inst.id} value={inst.id}>
                    {inst.nombre}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {destinoEsTemporal && (
              <Button
                icon={<EnvironmentOutlined />}
                style={{ marginBottom: 8 }}
                onClick={() => {
                  setMapMode("destino");
                  setMapVisible(true);
                }}
              >
                Buscar en el mapa (Destino)
              </Button>
            )}

            <Form.Item
              label="Departamento"
              name="destino_departamento"
              rules={[
                { required: true, message: "El departamento es obligatorio" },
              ]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Seleccione un departamento"
                onChange={async (val) => {
                  form.setFieldsValue({ destino_ciudad: null });
                  const c = await fetchCiudades(val);
                  setCiudadesDestino(c);
                }}
                filterOption={(input, option) => {
                  const label = option.label ? option.label.toLowerCase() : "";
                  const searchInput = input.toLowerCase();
                  return label.includes(searchInput);
                }}
                style={{ width: "100%" }}
              >
                {opcionesUbicacion(departamentos, 'departamento', 'destino').map(opcion => (
                  <Option key={opcion.value} value={opcion.value} label={opcion.label} disabled={opcion.disabled}>
                    {opcion.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Ciudad"
              name="destino_ciudad"
              rules={[{ required: true, message: "La ciudad es obligatoria" }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Seleccione una ciudad"
                filterOption={(input, option) => {
                  const label = option.label ? option.label.toLowerCase() : "";
                  const searchInput = input.toLowerCase();
                  return label.includes(searchInput);
                }}
                style={{ width: "100%" }}
              >
                {opcionesUbicacion(ciudadesDestino, 'ciudad', 'destino').map(opcion => (
                  <Option key={opcion.value} value={opcion.value} label={opcion.label} disabled={opcion.disabled}>
                    {opcion.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Dirección"
              name="destino_direccion"
              rules={[
                { required: true, message: "La dirección es obligatoria" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Localidad" name="destino_localidad">
              <Input />
            </Form.Item>
            <Form.Item
              label="Barrio"
              name="destino_barrio"
              rules={[{ required: true, message: "El barrio es obligatorio" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item label="Vereda/Corregimiento" name="destino_vereda_corregimiento">
              <Input placeholder="Opcional" />
            </Form.Item>
            <Form.Item label="Indicaciones" name="destino_indicaciones">
              <TextArea rows={2} />
            </Form.Item>
            <Form.Item
              label="Coordenadas (lat, lng)"
              name="destino_coordenadas"
              rules={[
                { required: true, message: "Las coordenadas son obligatorias" },
                coordinateValidationRule(true),
              ]}
            >
              <Input placeholder="Ej: 4.6097, -74.0817" />
            </Form.Item>
          </Col>
        </Row>

        {/* ============ Tarjetas Distancia / Tiempo ============ */}
        <Row gutter={[16, 16]} className="traslado-cards-row">
          <Col xs={24} sm={12}>
            <Card
              title={
                <span>
                  <EnvironmentOutlined className="traslado-card-icon" />
                  Distancia Aproximada
                </span>
              }
              variant="borderless"
              className="traslado-card"
            >
              <p className="traslado-card-text">
                {distance ? `${distance} km` : "Calculando..."}
              </p>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              title={
                <span>
                  <ClockCircleOutlined className="traslado-card-icon" />
                  Tiempo Estimado
                </span>
              }
              variant="borderless"
              className="traslado-card"
            >
              <p className="traslado-card-text">
                {duration
                  ? `${parseFloat(duration).toFixed(1)} min`
                  : "Calculando..."}
              </p>
            </Card>
          </Col>
        </Row>

        {/* ============ Observaciones del Traslado ============ */}
        <Title level={4} className="traslado-section-title">
          <FileTextOutlined className="traslado-section-icon" />
          Observaciones
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="observaciones_traslado"
              label="Observaciones del Traslado"
              rules={[
                {
                  max: 1000,
                  message:
                    "Las observaciones no pueden exceder los 1000 caracteres",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Ingresa las observaciones del traslado aquí..."
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="indicaciones_conductor"
              label="Observaciones del Conductor"
              rules={[
                {
                  max: 1000,
                  message:
                    "Las observaciones no pueden exceder los 1000 caracteres",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Ingresa las observaciones del conductor aquí..."
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ============ Botón para ver ruta ============ */}
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            <Button
              type="primary"
              className="traslado-map-button"
              onClick={() => {
                if (origin && destination) {
                  setMapMode(null);
                  setMapVisible(true);
                } else {
                  message.error("Coordenadas inválidas para ver la ruta.");
                }
              }}
            >
              Ver ruta en el mapa
            </Button>
          </Col>
        </Row>
      </Form>
      </div>
      </Spin>

      {/* Mapa para seleccionar coords */}
      <MapDialog
        open={mapVisible && mapMode !== null}
        onClose={handleCloseMap}
        onSelect={handleMapSelect}
        initialCoords=""
      />

      {/* Mapa para ver la ruta */}
      <MapRoute
        open={mapVisible && mapMode === null}
        onClose={handleCloseMap}
        origin={origin || { lat: 0, lng: 0 }}
        destination={destination || { lat: 0, lng: 0 }}
      />
    </Modal>
  );
}

EditarTrasladoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  traslado: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    // Campos del traslado...
    fecha: PropTypes.string,
    hora_recogida: PropTypes.string,
    hora_llegada: PropTypes.string,
    // hora_retorno_recogida: PropTypes.string,
    // hora_retorno_llegada: PropTypes.string,
    origen_lugar: PropTypes.string,
    origen_departamento: PropTypes.string,
    origen_ciudad: PropTypes.string,
    origen_localidad: PropTypes.string,
    origen_barrio: PropTypes.string,
    origen_vereda_corregimiento: PropTypes.string,
    origen_direccion: PropTypes.string,
    origen_indicaciones: PropTypes.string,
    origen_coordenadas: PropTypes.shape({
      type: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    destino_lugar: PropTypes.string,
    destino_departamento: PropTypes.string,
    destino_ciudad: PropTypes.string,
    destino_localidad: PropTypes.string,
    destino_barrio: PropTypes.string,
    destino_vereda_corregimiento: PropTypes.string,
    destino_direccion: PropTypes.string,
    destino_indicaciones: PropTypes.string,
    destino_coordenadas: PropTypes.shape({
      type: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    autorizacion_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // Nuevo campo agregado
    observaciones_traslado: PropTypes.string,
  }).isRequired,
  onUpdated: PropTypes.func,
};

export default EditarTrasladoDialog;
